import { CoherenceNav, INavLink, INavLinkGroup } from "@coherence-design-system/controls";
import { CoherenceTheme } from "@coherence-design-system/styles";
import { ThemeProvider } from "@fluentui/react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { UserPermission } from "../../services/AuthenticationService/AuthReducer";
import { AppName } from "../Shared/AppConstants";
import { RootContext } from "../Stores/RootStore";

// Interfaces //
interface INavProps {
    onNavCollapsed: (isCollapsed: boolean) => void;
    onNavItemClicked: () => void;
}

// Page Names
const home: string = "Home";
const onboardServers: string = "Onboard Assets";
const offboardServers: string = "Offboard Assets";
const manageServers: string = "Manage Assets";
const manageJobs: string = "Manage Jobs";
const jobHistory: string = "Job History";
const serviceadministration: string = "Service Admin";
const admin: string = "Admin";
const admindc: string = "Admin DC";

export const Nav: React.FunctionComponent<INavProps> = (props: INavProps) => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = React.useState<string>("");
    const { state } = React.useContext(RootContext);
    const navRef = React.useRef<HTMLElement>(null);
    const isNavVisible = state.AuthStore.UserPermitted === UserPermission.Permitted && !state.AuthStore.UserTimeOut;

    // Fix ADO #15693512 / #15693499: Keyboard access to hamburger menu items.
    // CoherenceNav adds a window-level keydown handler (bubble phase) that
    // dismisses the mobile nav panel on any Tab press unless focus is on the
    // hamburger button itself. This prevents keyboard users from tabbing
    // through collapsed nav items.
    //
    // Fix: stop Tab propagation at the <nav> wrapper so the window handler
    // never fires while focus is inside the nav. When focus leaves the nav
    // (focusout with relatedTarget outside), simulate an outside click so
    // CoherenceNav closes the panel normally.
    //
    // The effect depends on isNavVisible because the <nav> element is
    // conditionally rendered; with [] deps the ref would be null on mount
    // and the listener would never attach.
    React.useEffect(() => {
        const navElement = navRef.current;
        if (!navElement) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Tab") {
                event.stopPropagation();
            }
        };

        const handleFocusOut = (event: FocusEvent) => {
            const relatedTarget = event.relatedTarget as Node | null;
            if (relatedTarget && !navElement.contains(relatedTarget)) {
                // Focus moved outside the nav — close the mobile panel via
                // CoherenceNav's outside-click dismiss handler.
                document.body.dispatchEvent(new MouseEvent("click", { bubbles: true }));
            }
        };

        navElement.addEventListener("keydown", handleKeyDown);
        navElement.addEventListener("focusout", handleFocusOut);
        return () => {
            navElement.removeEventListener("keydown", handleKeyDown);
            navElement.removeEventListener("focusout", handleFocusOut);
        };
    }, [isNavVisible]);

    const initialLinks: INavLink[] = [{
        name: home,
        key: "homeKey",
        ariaLabel: "Link " + home,
        icon: home,
        "data-id": "automation_id_22230",
        target: "_blank",
        onClick: () => {
            setCurrentPage(home);
            props.onNavItemClicked();
            navigate("/");
        },
        isSelected: state.DataStore.CurrentPage == "home" ? true : false,
    },
    {
        name: onboardServers,
        id: "onboardingNavButton",
        key: "onboardingKey",
        ariaLabel: "Link " + onboardServers,
        icon: "AddIn",
        "data-id": "automation_id_22231",
        target: "_blank",
        onClick: () => {
            setCurrentPage(onboardServers);
            props.onNavItemClicked();
            navigate("/onboarding", {replace: true, state: { reload: (currentPage == onboardServers) }});
        },
        isSelected: state.DataStore.CurrentPage === "onboarding" ? true : false,
    },
    {
        name: manageServers,
        id: "manageServersNavButton",
        key: "manageServersKey",
        ariaLabel: "Link " + manageServers,
        icon: "Server",
        target: "_blank",
        onClick: () => {
            setCurrentPage(manageServers);
            navigate("/manageservers");
            props.onNavItemClicked();
        },
        isSelected: state.DataStore.CurrentPage === "manageservers"? true : false,
    },
    {
        name: manageJobs,
        id: "manageJobsNavButton",
        key: "manageJobsKey",
        ariaLabel: "Link " + manageJobs,
        icon: "ServerProcesses",
        target: "_blank",
        onClick: () => {
            setCurrentPage(manageJobs);
            navigate("/managejobs");
            props.onNavItemClicked();
        },
        isSelected: state.DataStore.CurrentPage === "managejobs" ? true : false,
    },
    {
        name: offboardServers,
        id: "offboardingNavButton",
        key: "offboardingKey",
        ariaLabel: "Link " + offboardServers,
        icon: "WebComponents",
        "data-id": "automation_id_22232",
        target: "_blank",
        onClick: () => {
            setCurrentPage(offboardServers);
            navigate("/offboarding");
            props.onNavItemClicked();
        },
        isSelected: state.DataStore.CurrentPage === "offboarding" ? true : false,
    },
    {
        name: serviceadministration,
        id: "serviceadministrationNavButton",
        key: "serviceadministrationKey",
        ariaLabel: "Link " + serviceadministration,
        icon: "UserEvent",
        "data-id": "automation_id_22235",
        target: "_blank",
        onClick: () => {
            setCurrentPage(serviceadministration);
            navigate("/serviceadministration");
            props.onNavItemClicked();
        },
        isSelected: state.DataStore.CurrentPage === "serviceadministration" ? true : false,
    }];

    // Admin-gated nav links. Visible only when the Admin Toggle in the header
    // is on (state.AuthStore.toggleAdmin === true). The toggle is itself only
    // shown to users where isAdmin is true, so toggleAdmin implies isAdmin.
    // Job History sits here because it is an admin-only view alongside the
    // Admin and Admin DC pages.
    const jobHistoryLink: INavLink = {
        name: jobHistory,
        id: "jobHistoryNavButton",
        key: "jobHistoryKey",
        ariaLabel: "Link " + jobHistory,
        icon: "History",
        target: "_blank",
        onClick: () => {
            setCurrentPage(jobHistory);
            navigate("/jobhistory");
            props.onNavItemClicked();
        },
        isSelected: state.DataStore.CurrentPage === "jobhistory" ? true : false,
    };

    const adminLinks: INavLink[] = [jobHistoryLink, {
        name: admin,
        id: "adminNavButton",
        key: "adminKey",
        ariaLabel: "Link " + admin,
        icon: "Admin",
        "data-id": "automation_id_22236",
        target: "_blank",
        onClick: () => {
            setCurrentPage(admin);
            navigate("/admin");
            props.onNavItemClicked();
        },
        isSelected: state.DataStore.CurrentPage === "admin" ? true : false,
    }];

    const admindclinks: INavLink[] = [{
        name: admindc,
        key: "admindcKey",
        ariaLabel: "Link " + admindc,
        icon: "Admin",
        "data-id": "automation_id_22237",
        target: "_blank",
        onClick: () => {
            setCurrentPage(admin);
            navigate("/admindc");
            props.onNavItemClicked();
        },
        isSelected: state.DataStore.CurrentPage === "admindc" ? true : false,
    }];

    const navGoupLinks: INavLinkGroup[] = [
        { key: "Left Nav Menu",
        links: state.AuthStore.toggleAdmin ? initialLinks.concat(adminLinks, admindclinks) : initialLinks,
    }];

    return (
        <ThemeProvider theme={CoherenceTheme}>
            {isNavVisible &&
                <nav ref={navRef} id="leftNav" role="navigation" aria-label="Main navigation">
                    <CoherenceNav
                        appName={AppName}
                        groups={navGoupLinks}
                        onNavCollapsed={props.onNavCollapsed}
                        toggleNavAriaLabel="Toggle navigation menu"
                        styles = {{
                            root: {
                                // Ensure hamburger menu is keyboard accessible
                                '& button': {
                                    minWidth: '44px',
                                    minHeight: '44px',
                                },
                                '& button:focus-visible': {
                                    outline: '2px solid #0078d4',
                                    outlineOffset: '2px',
                                }
                            },
                            mobileNavTrigger: {
                                minWidth: '44px',
                                minHeight: '44px',
                                '&:focus-visible': {
                                    outline: '2px solid #0078d4',
                                    outlineOffset: '2px',
                                }
                            }
                        }}
                    />
                </nav>
            }
        </ThemeProvider>
    );
};
